"use client";
import {
  useScroll,
  useTransform,
  motion,
} from "framer-motion";
import React, { useEffect, useRef, useState } from "react";

export const Timeline = ({ data }) => {
  const ref = useRef(null);
  const containerRef = useRef(null);
  const sectionRefs = useRef([]);
  const dotRefs = useRef([]);
  const contentRefs = useRef([]); // Track body content for trigger positioning
  const [height, setHeight] = useState(0);
  const [dotPositions, setDotPositions] = useState([]);

  // Freeze-frame state
  const [isLocked, setIsLocked] = useState(false);
  const [activeSection, setActiveSection] = useState(-1);
  const [sectionProgress, setSectionProgress] = useState({});
  const [completedSections, setCompletedSections] = useState(new Set());

  // Refs for immediate access in event handlers (avoid stale closures)
  const isLockedRef = useRef(false);
  const activeSectionRef = useRef(-1);
  const sectionProgressRef = useRef({});
  const completedSectionsRef = useRef(new Set());

  // Sync refs with state
  useEffect(() => {
    isLockedRef.current = isLocked;
  }, [isLocked]);

  useEffect(() => {
    activeSectionRef.current = activeSection;
  }, [activeSection]);

  useEffect(() => {
    sectionProgressRef.current = sectionProgress;
  }, [sectionProgress]);

  useEffect(() => {
    completedSectionsRef.current = completedSections;
  }, [completedSections]);

  // Calculate height and dot positions for progress bar checkpoints
  useEffect(() => {
    const updateMeasurements = () => {
      if (ref.current && contentRefs.current.length > 0) {
        const containerRect = ref.current.getBoundingClientRect();

        // Calculate dot positions based on content positions relative to container
        const positions = contentRefs.current.map((contentEl) => {
          if (!contentEl) return 0;
          // Get content rect and calculate position relative to container
          const contentRect = contentEl.getBoundingClientRect();
          // Position is content top relative to container top
          return contentRect.top - containerRect.top;
        });
        setDotPositions(positions);

        // Height ends at the last dot plus some padding
        const lastDotPos = positions[positions.length - 1];
        if (lastDotPos) {
          setHeight(lastDotPos + 100);
        }
      }
    };

    // Wait for layout to settle, then update
    const timer = setTimeout(updateMeasurements, 200);
    window.addEventListener('resize', updateMeasurements);
    // Also update when scroll settles (for initial calculation)
    window.addEventListener('load', updateMeasurements);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updateMeasurements);
    };
  }, [data]);

  // Progress line animation - synced with content scroll
  // Starts when container top reaches 30% down from viewport top
  // Ends when container bottom reaches 80% down from viewport top
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start 0.3", "end 0.8"],
  });

  // Transform scroll progress to height
  const heightTransform = useTransform(scrollYProgress, [0, 1], [0, height]);

  // Main scroll/wheel handler
  useEffect(() => {
    let lastTouchY = 0;

    const findSectionReadyForFreeze = () => {
      const viewportHeight = window.innerHeight;

      for (let i = 0; i < contentRefs.current.length; i++) {
        const contentEl = contentRefs.current[i];

        // Skip if no element or already completed
        if (!contentEl || completedSectionsRef.current.has(i)) continue;

        const contentRect = contentEl.getBoundingClientRect();

        // Simple trigger: Content (title + body) is centered in viewport
        // Title should be around 25-35% from top of screen
        const contentTop = contentRect.top;
        const isCentered = contentTop >= viewportHeight * 0.25 &&
                           contentTop <= viewportHeight * 0.35;

        if (isCentered) {
          return i;
        }
      }
      return -1;
    };

    const handleWheel = (e) => {
      // Use refs for immediate access to current values
      const currentLocked = isLockedRef.current;
      const currentSection = activeSectionRef.current;

      // If already locked, handle animation progress
      if (currentLocked && currentSection >= 0) {
        e.preventDefault();
        e.stopPropagation();

        const delta = e.deltaY;

        if (delta > 0) {
          // Scrolling DOWN - advance animation
          const currentProgress = sectionProgressRef.current[currentSection] || 0;
          const newProgress = Math.min(1, currentProgress + Math.abs(delta) / 400);

          // Update ref immediately
          sectionProgressRef.current = { ...sectionProgressRef.current, [currentSection]: newProgress };
          setSectionProgress(prev => ({ ...prev, [currentSection]: newProgress }));

          if (newProgress >= 1) {
            // Complete section - update refs immediately
            isLockedRef.current = false;
            activeSectionRef.current = -1;
            completedSectionsRef.current = new Set([...completedSectionsRef.current, currentSection]);

            setTimeout(() => {
              setCompletedSections(prev => new Set([...prev, currentSection]));
              setIsLocked(false);
              setActiveSection(-1);
            }, 100);
          }
        } else if (delta < 0) {
          // Scrolling UP - reverse animation
          const currentProgress = sectionProgressRef.current[currentSection] || 0;
          const newProgress = Math.max(0, currentProgress - Math.abs(delta) / 400);

          // Update ref immediately
          sectionProgressRef.current = { ...sectionProgressRef.current, [currentSection]: newProgress };
          setSectionProgress(prev => ({ ...prev, [currentSection]: newProgress }));

          if (newProgress <= 0) {
            // Unlock - update refs immediately
            isLockedRef.current = false;
            activeSectionRef.current = -1;

            setTimeout(() => {
              setIsLocked(false);
              setActiveSection(-1);
            }, 50);
          }
        }
        return;
      }

      // Not locked - check if we should lock
      const scrollY = window.scrollY;

      // Only engage after scrolling past header
      if (scrollY < 200) return;

      // Only lock on scroll DOWN
      if (e.deltaY <= 0) return;

      // Check if any section is ready for freeze-frame
      const sectionReady = findSectionReadyForFreeze();

      if (sectionReady >= 0) {
        // Lock and start animation for this section
        e.preventDefault();
        e.stopPropagation();

        // Update refs immediately
        isLockedRef.current = true;
        activeSectionRef.current = sectionReady;

        setActiveSection(sectionReady);
        setIsLocked(true);
        setSectionProgress(prev => ({
          ...prev,
          [sectionReady]: prev[sectionReady] || 0
        }));
      }
    };

    const handleTouchStart = (e) => {
      lastTouchY = e.touches[0].clientY;
    };

    const handleTouchMove = (e) => {
      const touchY = e.touches[0].clientY;
      const delta = lastTouchY - touchY;
      lastTouchY = touchY;

      const currentLocked = isLockedRef.current;
      const currentSection = activeSectionRef.current;

      if (currentLocked && currentSection >= 0) {
        e.preventDefault();

        if (delta > 0) {
          const currentProgress = sectionProgressRef.current[currentSection] || 0;
          const newProgress = Math.min(1, currentProgress + Math.abs(delta) / 120);

          sectionProgressRef.current = { ...sectionProgressRef.current, [currentSection]: newProgress };
          setSectionProgress(prev => ({ ...prev, [currentSection]: newProgress }));

          if (newProgress >= 1) {
            isLockedRef.current = false;
            activeSectionRef.current = -1;
            completedSectionsRef.current = new Set([...completedSectionsRef.current, currentSection]);

            setTimeout(() => {
              setCompletedSections(prev => new Set([...prev, currentSection]));
              setIsLocked(false);
              setActiveSection(-1);
            }, 100);
          }
        } else if (delta < 0) {
          const currentProgress = sectionProgressRef.current[currentSection] || 0;
          const newProgress = Math.max(0, currentProgress - Math.abs(delta) / 120);

          sectionProgressRef.current = { ...sectionProgressRef.current, [currentSection]: newProgress };
          setSectionProgress(prev => ({ ...prev, [currentSection]: newProgress }));

          if (newProgress <= 0) {
            isLockedRef.current = false;
            activeSectionRef.current = -1;

            setTimeout(() => {
              setIsLocked(false);
              setActiveSection(-1);
            }, 50);
          }
        }
      }
    };

    window.addEventListener('wheel', handleWheel, { passive: false });
    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: false });

    return () => {
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
    };
  }, []); // Register once on mount

  // Lock body scroll when freeze-frame is active
  useEffect(() => {
    if (isLocked) {
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
    } else {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    }

    return () => {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    };
  }, [isLocked]);

  return (
    <div
      className="timeline-wrapper w-full bg-white font-sans"
      ref={containerRef}
    >
      <div ref={ref} className="relative max-w-5xl mx-auto pb-20">
        {/* Timeline track container - holds line and dots */}
        <div className="absolute left-8 md:left-12 top-0" style={{ width: '3px' }}>
          {/* Background track line */}
          <div
            style={{ height: height + "px" }}
            className="w-full bg-neutral-200 rounded-full"
          />

          {/* Animated progress line */}
          <motion.div
            style={{ height: heightTransform }}
            className="absolute top-0 left-0 w-full rounded-full overflow-hidden"
          >
            <div
              className="w-full h-full"
              style={{
                background: 'linear-gradient(to bottom, rgba(255,255,255,1) 0%, rgba(255,255,255,0) 30px, #8b5cf6 60px, #3b82f6 100%)',
              }}
            />
          </motion.div>

          {/* Dots positioned on the line */}
          {data.map((item, index) => (
            <div
              key={`dot-${index}`}
              ref={(el) => (dotRefs.current[index] = el)}
              className="absolute left-1/2 -translate-x-1/2 h-4 w-4 md:h-5 md:w-5 rounded-full bg-white border-[3px] border-neutral-300 z-10"
              style={{ top: dotPositions[index] ? dotPositions[index] - 8 : 0 }}
            />
          ))}
        </div>

        {/* Content sections */}
        {data.map((item, index) => (
          <div
            key={index}
            ref={(el) => (sectionRefs.current[index] = el)}
            className="relative"
            style={{ minHeight: "90vh", paddingTop: "30vh", paddingBottom: "35vh" }}
          >
            {/* Content - offset from the timeline */}
            <div
              ref={(el) => (contentRefs.current[index] = el)}
              className="ml-20 md:ml-28 pr-4 md:pr-8"
            >
              {/* Title */}
              <h3 className="text-xl md:text-2xl lg:text-3xl font-bold text-neutral-500 mb-4 md:mb-6">
                {item.title}
              </h3>

              {/* Body */}
              <div className="max-w-3xl">
                {React.isValidElement(item.content)
                  ? React.cloneElement(item.content, {
                      freezeProgress: sectionProgress[index],
                      isActive: activeSection === index,
                      isCompleted: completedSections.has(index),
                    })
                  : item.content
                }
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
